import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Auth strategy (post v2.2 hardening):
 *   - Access + refresh tokens live in httpOnly cookies set by backend
 *     (mitigates XSS exfiltration).
 *   - CSRF protection via double-submit cookie:
 *       * blnx_csrf / blnx_m_csrf cookie is JS-readable
 *       * frontend echoes its value in `X-CSRF-Token` header on mutating
 *         requests; backend compares the two and rejects if they differ.
 *   - Non-sensitive user metadata (id, name, email, role, merchant) lives in
 *     sessionStorage so the SPA can render the avatar/sidebar synchronously
 *     without a /me round-trip on each navigation.
 *   - When the UI and API are on different origins (e.g. localhost →
 *     api-test.blonix.io), browsers won't store/send API cookies; login then
 *     also keeps access_token + csrf_token from the JSON body for Bearer auth.
 */

const SAFE_METHODS = new Set(["get", "head", "options"]);

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[$()*+./?[\\\]^{|}-]/g, "\\$&") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function csrfToken(cookieName, storageKey) {
  return readCookie(cookieName) || sessionStorage.getItem(storageKey);
}

function attachCsrf(cookieName, storageKey) {
  return (config) => {
    if (!SAFE_METHODS.has(String(config.method || "get").toLowerCase())) {
      const tok = csrfToken(cookieName, storageKey);
      if (tok) config.headers["X-CSRF-Token"] = tok;
    }
    return config;
  };
}

/** Bearer from login body when API cookies are on another origin (local UI → remote API). */
function attachBearer(accessStorageKey) {
  return (config) => {
    const token = sessionStorage.getItem(accessStorageKey);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  };
}

function persistSessionFromLogin(r, { userKey, csrfKey, accessKey, csrfCookie }) {
  sessionStorage.setItem(userKey, JSON.stringify(r.data.user));
  if (r.data.csrf_token) sessionStorage.setItem(csrfKey, r.data.csrf_token);
  // Cookies are host-bound to the API; localhost cannot read them cross-origin.
  if (r.data.access_token && !readCookie(csrfCookie)) {
    sessionStorage.setItem(accessKey, r.data.access_token);
  }
}

function clearAdminSession() {
  sessionStorage.removeItem("blnx_user");
  sessionStorage.removeItem("blnx_csrf_token");
  sessionStorage.removeItem("blnx_access_token");
}

function clearMerchantSession() {
  sessionStorage.removeItem("blnx_m_user");
  sessionStorage.removeItem("blnx_m_csrf_token");
  sessionStorage.removeItem("blnx_m_access_token");
}

function hasAdminSession() {
  if (!sessionStorage.getItem("blnx_user")) return false;
  if (sessionStorage.getItem("blnx_access_token")) return true;
  return !!csrfToken("blnx_csrf", "blnx_csrf_token");
}

function hasMerchantSession() {
  if (!sessionStorage.getItem("blnx_m_user")) return false;
  if (sessionStorage.getItem("blnx_m_access_token")) return true;
  return !!csrfToken("blnx_m_csrf", "blnx_m_csrf_token");
}

// ---- Admin axios instance ----
export const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use(attachBearer("blnx_access_token"));
api.interceptors.request.use(attachCsrf("blnx_csrf", "blnx_csrf_token"));
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearAdminSession();
      if (!window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/m/")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const auth = {
  login: async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    persistSessionFromLogin(r, {
      userKey: "blnx_user",
      csrfKey: "blnx_csrf_token",
      accessKey: "blnx_access_token",
      csrfCookie: "blnx_csrf",
    });
    return r.data;
  },
  logout: async () => {
    try { await api.post("/auth/logout"); } catch { /* still clear local state */ }
    clearAdminSession();
  },
  user: () => {
    try { return JSON.parse(sessionStorage.getItem("blnx_user") || "null"); }
    catch { return null; }
  },
  isAuthed: () => hasAdminSession(),
  refreshCsrf: async () => {
    const r = await api.get("/auth/csrf");
    if (r.data?.csrf_token) sessionStorage.setItem("blnx_csrf_token", r.data.csrf_token);
    return r.data?.csrf_token;
  },
};

// ---- Merchant axios instance ----
export const mApi = axios.create({ baseURL: API, withCredentials: true });
mApi.interceptors.request.use(attachBearer("blnx_m_access_token"));
mApi.interceptors.request.use(attachCsrf("blnx_m_csrf", "blnx_m_csrf_token"));
mApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearMerchantSession();
      if (window.location.pathname.startsWith("/m/")
          && window.location.pathname !== "/m/login"
          && window.location.pathname !== "/m/signup") {
        window.location.href = "/m/login";
      }
    }
    return Promise.reject(err);
  }
);

export const mAuth = {
  login: async (email, password) => {
    const r = await mApi.post("/merchant/auth/login", { email, password });
    persistSessionFromLogin(r, {
      userKey: "blnx_m_user",
      csrfKey: "blnx_m_csrf_token",
      accessKey: "blnx_m_access_token",
      csrfCookie: "blnx_m_csrf",
    });
    return r.data;
  },
  signup: async (payload) => {
    const r = await axios.post(`${API}/public/merchants/signup`, payload, { withCredentials: true });
    return r.data;
  },
  logout: async () => {
    try { await mApi.post("/merchant/auth/logout"); } catch { /* still clear local state */ }
    clearMerchantSession();
  },
  user: () => {
    try { return JSON.parse(sessionStorage.getItem("blnx_m_user") || "null"); }
    catch { return null; }
  },
  isAuthed: () => hasMerchantSession(),
  refreshCsrf: async () => {
    const r = await mApi.get("/merchant/auth/csrf");
    if (r.data?.csrf_token) sessionStorage.setItem("blnx_m_csrf_token", r.data.csrf_token);
    return r.data?.csrf_token;
  },
};
